import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import t from '../language/Ko-kr.json';
import Alarm from './alarm/alarm';
import { ResponsiveShell } from '../components/ResponsiveShell';
import { WidgetContainer, WIDGET_DEFS, WidgetLayoutData, WidgetPreview, WidgetSize } from './widget/widget';
import { FullScreenCalendar } from './widget/calender';
import JournalApp from './journal/journal';

/* ─── Animations ─────────────────────────────────────────────────────────── */
const floatAnim = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50%       { transform: translateY(-14px) rotate(2deg); }
`;

const bounceIn = keyframes`
  0%   { transform: scale(0.85); opacity: 0; }
  60%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); }
`;

const fadeSlideUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

/* ─── Page Transition Overlay ────────────────────────────────────────────── */
const PageFadeOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: #F0EBE0;
  z-index: 99999;
  pointer-events: ${p => p.$visible ? 'auto' : 'none'};
  opacity: ${p => p.$visible ? 1 : 0};
  transition: opacity 0.32s cubic-bezier(0.4, 0, 0.2, 1);
`;



/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; user-select: none; }
  html, body {
    height: 100%;
    overflow: hidden;
    overscroll-behavior-x: none;
  }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: var(--app-height, 100dvh);
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: var(--app-height, 100dvh);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    overscroll-behavior-x: none;
  }
`;


/* ─── Tablet UI ──────────────────────────────────────────────────────────── */
const BackgroundWallpaper = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
    z-index: -1;
  }
`;

const TabletDesktopArea = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: auto;
  box-sizing: border-box;
  overflow-y: hidden;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 140px;
  touch-action: none;
  overscroll-behavior-x: contain;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (pointer: fine) {
    cursor: grab;
    &:active { cursor: grabbing; }
  }
`;

const PageContainer = styled.div`
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  overflow-y: hidden;

  @media (min-width: 768px) {
    padding: 60px;
  }
`;

const PageIndicatorContainer = styled.div<{ $isEditMode: boolean }>`
  position: absolute;
  top: auto;
  bottom: ${p => p.$isEditMode ? '174px' : '140px'};
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;
  pointer-events: none;

`;

const Dot = styled.div<{ $active: boolean }>`
  width: ${p => p.$active ? '8px' : '6px'};
  height: ${p => p.$active ? '8px' : '6px'};
  border-radius: 50%;
  background: ${p => p.$active ? '#3D3020' : 'rgba(0,0,0,0.2)'};
  transition: all 0.2s ease;
  pointer-events: auto;
  cursor: pointer;
`;

/* 위젯 영역과 앱 아이콘 영역이 같은 그리드 단위(행 높이/간격)를 공유하도록 상수로 통일.
   4-column grid. Each "unit" cell = 1 col wide, GRID_ROW_H tall.
   small  = 2 cols × 2 rows
   medium = 4 cols × 2 rows – full-width banner
   large  = 4 cols × 4 rows – full-width tall */
const GRID_ROW_H = 100;       // px per unit row (위젯/앱 공통)
const GRID_ROW_GAP = 16;      // px 세로 간격 (위젯/앱 공통)
const GRID_COL_GAP = 12;      // px 가로 간격 (위젯/앱 공통)
const GRID_MAX_WIDTH = 1000;  // px (위젯/앱 공통)
const WIDGET_SIZE_RANK: Record<WidgetSize, number> = { small: 0, medium: 1, large: 2 };
const GRID_COLS = 4;
const GRID_ROWS = 6;
const GRID_PAGE_SLOTS = GRID_COLS * GRID_ROWS;
const MIN_DESKTOP_PAGES = 2;

const DesktopPageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: clamp(54px, calc((var(--app-height, 100dvh) - 310px) / 6), ${GRID_ROW_H}px);
  grid-auto-flow: row;
  gap: ${GRID_ROW_GAP}px ${GRID_COL_GAP}px;
  width: 100%;
  max-width: ${GRID_MAX_WIDTH}px;
  align-content: start;
  justify-items: stretch;
  position: relative;
`;

const GridDropCell = styled.div`
  border-radius: 18px;
  z-index: 0;
  transition: background 0.16s ease, box-shadow 0.16s ease;
`;


/* ─── Status Bar ─────────────────────────────────────────────────────────── */
const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 28px 8px;
  color: #A09070;
  font-family: 'Jua', sans-serif;
  font-size: 14px;
  flex-shrink: 0;
  position: relative;
`;

const StatusTime = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const StatusSignal = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2.5px;
  height: 14px;
`;
const Bar = styled.div<{ $h: number }>`
  width: 3px;
  height: ${p => p.$h}px;
  background: #B0A080;
  border-radius: 2px;
`;

const StatusLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #B0A080;
`;

/* ─── Hero Area ──────────────────────────────────────────────────────────── */
const HeroArea = styled.div`
  flex: 1;
  @media (min-width: 768px) {
    display: none !important;
  }
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 24px 100px;
  gap: 36px;
  min-height: 0;
`;

const HeroTitleText = styled.h1`
  font-family: 'Jua', sans-serif;
  font-size: clamp(22px, 6.5vw, 28px);
  color: #3D3020;
  text-align: center;
  line-height: 1.4;
  white-space: pre-line;
  word-break: keep-all;
  animation: ${fadeSlideUp} 0.6s ease both;
  animation-delay: 0.1s;
`;

const MascotWrapper = styled.div`
  position: relative;
  cursor: pointer;
  animation: ${bounceIn} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  animation-delay: 0.2s;

  &:active { transform: scale(0.96); }
`;

const MascotImg = styled.img`
  width: 340px;
  height: 340px;
  object-fit: contain;
  filter: drop-shadow(0 16px 36px rgba(90, 60, 30, 0.2));
  animation: ${floatAnim} 4.5s ease-in-out infinite;
  display: block;
`;

const MascotHint = styled.div`
  margin-top: -8px;
  font-family: 'Jua', sans-serif;
  font-size: 14px;
  color: #B0A080;
  text-align: center;
  background: #EDE9D5;
  border-radius: 100px;
  padding: 8px 20px;
  animation: ${fadeSlideUp} 0.6s ease both;
  animation-delay: 0.4s;
`;



/* ─── Write Letter FAB ─────────────────────────────────────────────────────── */
const WriteFab = styled.button`
  position: absolute;
  @media (min-width: 768px) {
    display: none !important;
  }
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: #E07040;
  color: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(224, 112, 64, 0.38);
  animation: ${bounceIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  animation-delay: 0.6s;
  z-index: 50;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;

  &:hover {
    background: #C85E30;
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 10px 24px rgba(224, 112, 64, 0.45);
  }
  &:active { transform: scale(0.92); }

  svg { 
    width: 24px; 
    height: 24px; 
    stroke-width: 2.2; 
    transform: translate(-1px, 1px);
  }
`;

/* ─── App Dock (bottom grid) ─────────────────────────────────────────────── */
const DockArea = styled.div`
  position: absolute;
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 32px;
  padding: 16px 24px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  width: max-content;
  min-width: 200px;
  animation: ${fadeSlideUp} 0.6s ease both;
  animation-delay: 0.3s;
  z-index: 50;
`;

const DockLabel = styled.p`
  font-family: 'Jua', sans-serif;
  font-size: 11px;
  color: #B0A080;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 14px;
  text-align: center;
`;

const AppGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  @media (min-width: 768px) {
    gap: 24px;
  }
`;

const ToastMsg = styled.div<{ $show: boolean }>`
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  white-space: nowrap;
  opacity: ${p => p.$show ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.3s;
  z-index: 200;
`;

const jiggle = keyframes`
  0%, 100% { transform: rotate(-0.6deg); }
  50% { transform: rotate(0.6deg); }
`;

const holdPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(224, 112, 64, 0.5); }
  50%       { box-shadow: 0 0 0 10px rgba(224, 112, 64, 0); }
`;

/* ─── Hold Progress Ring ────────────────────────────────────────────────── */
const HoldRingWrapper = styled.div`
  position: absolute;
  top: -6px; left: -6px;
  width: calc(100% + 12px);
  height: calc(100% + 12px);
  pointer-events: none;
`;

const HoldRingSvg = styled.svg<{ $progress: number }>`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  circle:last-child {
    stroke-dashoffset: ${p => 100 - p.$progress};
    transition: stroke-dashoffset 0.05s linear;
  }
`;

/* ─── Edit Done Button (소형 pill) ──────────────────────────────────────── */
const EditDoneBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #E07040;
  color: #fff;
  border: none;
  border-radius: 100px;
  padding: 5px 14px;
  font-family: 'Jua', sans-serif;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  box-shadow: 0 2px 8px rgba(224, 112, 64, 0.4);
  &:hover { background: #C85E30; transform: translateY(-1px); }
  &:active { transform: scale(0.97); }
`;

const EditBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 0 2px;
`;

const AppItem = styled(motion.div).attrs({
  transition: { type: 'spring', stiffness: 700, damping: 40 }
}) <{ $isDragged?: boolean; $isDragInProgress?: boolean }>`
  ${p => p.$isDragInProgress && `
    * { pointer-events: none; }
  `}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 6px;
  cursor: pointer;
  opacity: ${p => p.$isDragged ? 0.4 : 1};
`;

const AppItemContent = styled.div<{ $delay: number; $isEditMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  animation-name: ${bounceIn}, ${p => p.$isEditMode ? jiggle : 'none'};
  animation-duration: 0.5s, 0.55s;
  animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1), ease-in-out;
  animation-iteration-count: 1, infinite;
  animation-fill-mode: both, none;
  animation-delay: ${p => `${0.35 + p.$delay * 0.06}s`}, 0s;
  animation-play-state: running;

  @media (prefers-reduced-motion: reduce) {
    animation-name: ${bounceIn}, none;
  }

  &:active > div { transform: ${p => p.$isEditMode ? 'none' : 'scale(0.92)'}; }
`;

const BadgeBtn = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: #E07040;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  z-index: 30;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  line-height: 1;
`;

const AppIcon = styled.div<{ $color: string; $isEditMode?: boolean }>`
  position: relative;
  width: clamp(56px, 14vw, 72px);
  height: clamp(56px, 14vw, 72px);
  background: ${p => p.$color};
  border-radius: clamp(16px, 4vw, 22px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(90, 60, 30, 0.12);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s;

  ${AppItem}:hover & {
    transform: ${p => p.$isEditMode ? 'none' : 'translateY(-4px) scale(1.08)'};
    box-shadow: ${p => p.$isEditMode ? '0 4px 12px rgba(90, 60, 30, 0.12)' : '0 10px 20px rgba(90, 60, 30, 0.18)'};
  }

  svg { 
    width: clamp(26px, 6.5vw, 34px); 
    height: clamp(26px, 6.5vw, 34px); 
  }
`;

const AppName = styled.span`
  font-family: 'Jua', sans-serif;
  font-size: clamp(10px, 2.5vw, 13px);
  color: #7A6A50;
  text-align: center;
  line-height: 1.2;
`;

/* ─── Folder Modal ───────────────────────────────────────────────────────── */
const FolderOverlay = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$isOpen ? 1 : 0};
  pointer-events: ${p => p.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s;
`;

const FolderContainer = styled(motion.div)`
  width: 280px;
  background: rgba(249, 246, 232, 0.95);
  border-radius: 32px;
  padding: 24px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FolderHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const FolderTitle = styled.h3`
  font-family: 'Jua', sans-serif;
  font-size: 16px;
  color: #5C5040;
  text-align: center;
`;

const FolderSubtitle = styled.span`
  font-family: 'Jua', sans-serif;
  font-size: 11px;
  color: rgba(92, 80, 64, 0.6);
  text-align: center;
`;

const FolderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 12px;
`;

/* ─── Beta Notice Modal ─────────────────────────────────────────────────── */
const NoticeOverlay = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$isOpen ? 1 : 0};
  pointer-events: ${p => p.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s;
`;

const NoticeCard = styled(motion.div)`
  width: 85%;
  max-width: 360px;
  background: #FDFBF4;
  border-radius: 24px;
  padding: 32px 24px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const NoticeDesc = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14.5px;
  color: #5C5040;
  margin-bottom: 24px;
  line-height: 1.6;
  white-space: pre-line;
  word-break: keep-all;
`;

const NoticeBtn = styled.button`
  width: 100%;
  background: #E07040;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(224, 112, 64, 0.3);
  transition: transform 0.15s, background 0.2s;

  &:hover { background: #D06030; transform: translateY(-2px); }
  &:active { transform: translateY(1px); }
`;

const DiscordBtn = styled.a`
  width: 100%;
  background: #5865F2;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(88, 101, 242, 0.3);
  transition: transform 0.15s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  margin-bottom: 12px;

  &:hover { background: #4752C4; transform: translateY(-2px); }
  &:active { transform: translateY(1px); }
`;

/* ─── App Data ───────────────────────────────────────────────────────────── */
type AppItemType = {
  id: string;
  name: string;
  color: string;
  href?: string;
  action?: () => void;
  icon: React.ReactNode;
  isFolder?: boolean;
};

const INITIAL_MAIN_APPS: AppItemType[] = [
  {
    id: 'write',
    name: t.home.apps.write,
    color: '#FFCA6A',
    href: '/write',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7V4h16v3M4 7v13h16V7M4 7l8 6 8-6" />
      </svg>
    ),
  },
  {
    id: 'archive',
    name: t.home.apps.archive,
    color: '#3DBBB0',
    href: '/letterbox',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    ),
  },
  {
    id: 'friend',
    name: t.home.apps.friend,
    color: '#60C080',
    href: '/friend',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    )
  },
];

const MORE_APP: AppItemType = {
  id: 'more',
  name: t.home.apps.more,
  color: '#C8C0A8',
  isFolder: true,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="14" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
    </svg>
  ),
};

const INITIAL_FOLDER_APPS: AppItemType[] = [
  {
    id: 'widget_settings',
    name: '위젯 변경',
    color: '#80C0D0',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="9" y1="21" x2="9" y2="9" />
      </svg>
    ),
  },
  {
    id: 'profile',
    name: t.home.apps.profile,
    color: '#C6A1F3',
    href: '/profile',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    id: 'decorateMailbox',
    name: t.home.apps.decorateMailbox,
    color: '#F49CC9',
    href: '#',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9Z" />
        <path d="M2 9V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3" />
        <path d="M10 14h4" />
        <path d="M12 14v4" />
      </svg>
    ),
  },
  {
    id: 'settings',
    name: t.home.apps.settings,
    color: '#A0B8D0',
    href: '/setting',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },

  {
    id: 'tracking',
    name: t.home.apps.tracking,
    color: '#F99570',
    href: '/archive',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="10" r="3" />
        <path d="M12 2a8 8 0 0 0-8 8c0 5.4 7.05 11.5 7.65 12 .2.17.5.17.7 0C13 21.5 20 15.4 20 10a8 8 0 0 0-8-8z" />
      </svg>
    ),
  },

  {
    id: 'beta_history',
    name: '베타 내역',
    color: '#65A3FF',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },

  {
    id: 'journal',
    name: '일기장',
    color: '#FFE299',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
      </svg>
    ),
  },

  {
    id: 'logout',
    name: '로그아웃',
    color: '#D06060',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    ),
    action: () => { (window as any).__neuritLogout?.(); }
  }
];

/* ─── Component ──────────────────────────────────────────────────────────── */
/* ─── Layout API helpers ─────────────────────────────────────────────────── */
const API_BASE = process.env.REACT_APP_API_URL || '';

const ALL_APPS: AppItemType[] = [...INITIAL_MAIN_APPS, ...INITIAL_FOLDER_APPS];
const DEFAULT_MAIN_APP_IDS = ['write', 'archive', 'friend', 'settings'];
const DEFAULT_WIDGET_LAYOUT: WidgetLayoutData[] = [
  { id: 'default-journal-list', type: 'journal-history', size: 'medium', page: 0, row: 0, col: 0 },
  { id: 'default-calendar', type: 'date', size: 'medium', page: 0, row: 2, col: 0 },
  { id: 'default-delivery-tracking', type: 'delivery-tracking', size: 'medium', page: 0, row: 4, col: 0 },
];

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

const fetchBtnLayout = async (): Promise<string[] | null> => {
  try {
    const res = await fetch(`${API_BASE}/user/me`, { headers: getAuthHeaders() });
    if (!res.ok) return null;
    const data = await res.json();

    if (data.btn_layout) {
      if (Array.isArray(data.btn_layout)) {
        return data.btn_layout;
      }
      if (typeof data.btn_layout === 'string') {
        try { return JSON.parse(data.btn_layout); } catch { return null; }
      }
    }
    return null;
  } catch {
    return null;
  }
};

const saveBtnLayout = async (main: string[]): Promise<boolean> => {
  try {
    const res = await fetch(`${API_BASE}/user/me/btn-layout`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ btn_layout: main }),
    });
    if (!res.ok) {
      console.error('Failed to save btn-layout. Status:', res.status, await res.text());
      return false;
    }
    return true;
  } catch (e) {
    console.error('Failed to save btn-layout:', e);
    return false;
  }
};

/* ─── Beta Unread Dot ───────────────────────────────────────────────────── */
const LATEST_BETA_VERSION = 'V1.0.2';
const BETA_SEEN_KEY = 'beta_seen_version';
const DESKTOP_LAYOUT_SEPARATOR = '__DESKTOP__';
const WIDGET_LAYOUT_SEPARATOR = '__WIDGETS__';

const UnreadDot = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 10px;
  height: 10px;
  background: #E03030;
  border-radius: 50%;
  border: 2px solid #F9F6E8;
  z-index: 20;
  pointer-events: none;
`;

const padWidgetsToGrid = (widgets: WidgetLayoutData[]) =>
  widgets.filter(widget => !widget.id.startsWith('__EMPTY__'));

/**
 * 모바일의 시스템/브라우저 뒤로가기가 홈 뒤의 인증 화면으로 이동하지 않고
 * 현재 떠 있는 오버레이만 닫도록 오버레이를 history 항목으로 관리한다.
 *
 * history 변경은 effect가 아니라 실제 open 동작에서만 수행해서 StrictMode의
 * effect 재실행으로 기록이 중복되는 것도 피한다.
 */
const useHistoryBackedOverlay = (name: string) => {
  const [isOpen, setIsOpen] = useState(false);
  const markerRef = useRef<string | null>(null);

  const open = useCallback(() => {
    if (markerRef.current) return;

    const marker = `${name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const currentState = window.history.state;
    const state = currentState && typeof currentState === 'object' ? currentState : {};
    window.history.pushState({ ...state, __neuritOverlay: marker }, '', window.location.href);
    markerRef.current = marker;
    setIsOpen(true);
  }, [name]);

  const close = useCallback(() => {
    const marker = markerRef.current;
    if (!marker) {
      setIsOpen(false);
      return;
    }

    if (window.history.state?.__neuritOverlay === marker) {
      // popstate에서 상태를 닫아 시스템 뒤로가기와 동일한 흐름을 탄다.
      window.history.back();
    } else {
      markerRef.current = null;
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (!markerRef.current) return;
      markerRef.current = null;
      setIsOpen(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return [isOpen, open, close] as const;
};

/**
 * iOS Safari/PWA의 화면 가장자리 뒤로가기 제스처가 홈을 벗어나 인증 화면이나
 * 이전 페이지를 다시 노출하지 않도록, 홈 주소 안에 항상 한 단계의 경계를 둔다.
 */
const useHomeHistoryBoundary = () => {
  useEffect(() => {
    const boundary = '__neuritHomeBoundary';
    const currentState = window.history.state;
    const state = currentState && typeof currentState === 'object' ? currentState : {};

    if (state[boundary] !== 'guard') {
      if (state[boundary] !== 'root') {
        window.history.replaceState({ ...state, [boundary]: 'root' }, '', window.location.href);
      }
      window.history.pushState({ ...state, [boundary]: 'guard' }, '', window.location.href);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.[boundary] !== 'root') return;
      window.history.pushState(
        { ...event.state, [boundary]: 'guard' },
        '',
        window.location.href
      );
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
};

const createEmptyApp = (index: number): AppItemType => ({
  id: `__EMPTY__${index}`,
  name: '',
  color: 'transparent',
  icon: null
} as AppItemType);

// 최소 두 페이지 분량을 유지해 위젯이 없거나 큰 화면이어도 다음 페이지로
// 앱을 옮길 수 있게 한다.
const padToGrid = (apps: AppItemType[], totalSlots = GRID_PAGE_SLOTS * MIN_DESKTOP_PAGES) => {
  // 빈 슬롯 ID는 저장 API의 중복 검사에 걸리지 않도록 위치별로 고유하게 유지한다.
  const padded = apps.map((app, index) => app.id.startsWith('__EMPTY__') ? createEmptyApp(index) : app);
  for (let i = padded.length; i < totalSlots; i++) {
    padded.push(createEmptyApp(i));
  }
  return padded;
};

const widgetDimensions = (size: WidgetSize) => ({
  width: size === 'small' ? 2 : 4,
  height: size === 'large' ? 4 : 2,
});

const widgetCells = (widget: WidgetLayoutData) => {
  if (widget.page == null || widget.row == null || widget.col == null) return [];
  const { width, height } = widgetDimensions(widget.size);
  const cells: number[] = [];
  for (let y = widget.row; y < widget.row + height; y++) {
    for (let x = widget.col; x < widget.col + width; x++) {
      cells.push(widget.page * GRID_PAGE_SLOTS + y * GRID_COLS + x);
    }
  }
  return cells;
};

const isValidWidgetPosition = (widget: WidgetLayoutData) => {
  if (!Number.isInteger(widget.page) || !Number.isInteger(widget.row) || !Number.isInteger(widget.col)) return false;
  const { width, height } = widgetDimensions(widget.size);
  return widget.page! >= 0 && widget.row! >= 0 && widget.col! >= 0 &&
    widget.row! + height <= GRID_ROWS && widget.col! + width <= GRID_COLS;
};

// 구형 순서 기반 배치를 한 번만 실제 좌표로 변환한다.
const migrateWidgetsToCoordinates = (widgets: WidgetLayoutData[]) => {
  const occupied = new Set<number>();
  return [...widgets]
    .sort((a, b) => WIDGET_SIZE_RANK[a.size] - WIDGET_SIZE_RANK[b.size])
    .map(widget => {
      const { width, height } = widgetDimensions(widget.size);
      for (let page = 0; ; page++) {
        for (let row = 0; row <= GRID_ROWS - height; row++) {
          for (let col = 0; col <= GRID_COLS - width; col++) {
            const candidate = { ...widget, page, row, col };
            const cells = widgetCells(candidate);
            if (cells.every(cell => !occupied.has(cell))) {
              cells.forEach(cell => occupied.add(cell));
              return candidate;
            }
          }
        }
      }
    });
};

const placeAppsAroundWidgets = (apps: AppItemType[], widgets: WidgetLayoutData[]) => {
  const realApps = apps.filter(app => !app.id.startsWith('__EMPTY__'));
  const occupied = new Set(widgets.flatMap(widgetCells));
  const requiredPages = Math.max(
    MIN_DESKTOP_PAGES,
    ...widgets.map(widget => (widget.page ?? 0) + 1),
    Math.ceil((realApps.length + occupied.size) / GRID_PAGE_SLOTS)
  );
  const slots = padToGrid([], requiredPages * GRID_PAGE_SLOTS);
  let appIndex = 0;
  for (let slot = 0; slot < slots.length && appIndex < realApps.length; slot++) {
    if (!occupied.has(slot)) slots[slot] = realApps[appIndex++];
  }
  while (appIndex < realApps.length) {
    const start = slots.length;
    slots.push(...padToGrid([], GRID_PAGE_SLOTS).map((_, index) => createEmptyApp(start + index)));
    for (let slot = start; slot < slots.length && appIndex < realApps.length; slot++) {
      if (!occupied.has(slot)) slots[slot] = realApps[appIndex++];
    }
  }
  return slots.map((app, index) => app.id.startsWith('__EMPTY__') ? createEmptyApp(index) : app);
};

const createDefaultMainApps = () => DEFAULT_MAIN_APP_IDS
  .map(id => ALL_APPS.find(app => app.id === id))
  .filter(Boolean) as AppItemType[];

const createDefaultFolderApps = () => {
  const slots = padToGrid([]);
  const appById = new Map(INITIAL_FOLDER_APPS.map(app => [app.id, app]));
  const placements: Record<number, string> = {
    2: 'profile',
    3: 'widget_settings',
    6: 'tracking',
    7: 'journal',
    24: 'decorateMailbox',
    25: 'beta_history',
    26: 'logout',
  };
  Object.entries(placements).forEach(([slot, id]) => {
    const app = appById.get(id);
    if (app) slots[Number(slot)] = app;
  });
  return slots;
};

const resolveWidgetCollisions = (widgets: WidgetLayoutData[], pinnedId: string) => {
  const pinned = widgets.find(widget => widget.id === pinnedId);
  if (!pinned) return widgets;

  const occupied = new Set(widgetCells(pinned));
  const resolved = new Map<string, WidgetLayoutData>([[pinned.id, pinned]]);
  const others = widgets
    .filter(widget => widget.id !== pinnedId)
    .sort((a, b) => {
      const aSlot = (a.page ?? 0) * GRID_PAGE_SLOTS + (a.row ?? 0) * GRID_COLS + (a.col ?? 0);
      const bSlot = (b.page ?? 0) * GRID_PAGE_SLOTS + (b.row ?? 0) * GRID_COLS + (b.col ?? 0);
      return aSlot - bSlot;
    });

  others.forEach(widget => {
    const currentCells = widgetCells(widget);
    if (isValidWidgetPosition(widget) && currentCells.every(cell => !occupied.has(cell))) {
      currentCells.forEach(cell => occupied.add(cell));
      resolved.set(widget.id, widget);
      return;
    }

    const { width, height } = widgetDimensions(widget.size);
    const startSlot = Math.max(0,
      (widget.page ?? 0) * GRID_PAGE_SLOTS + (widget.row ?? 0) * GRID_COLS + (widget.col ?? 0)
    );
    for (let candidateSlot = startSlot; ; candidateSlot++) {
      const page = Math.floor(candidateSlot / GRID_PAGE_SLOTS);
      const pageSlot = candidateSlot % GRID_PAGE_SLOTS;
      const row = Math.floor(pageSlot / GRID_COLS);
      const col = pageSlot % GRID_COLS;
      if (row + height > GRID_ROWS || col + width > GRID_COLS) continue;
      const candidate = { ...widget, page, row, col };
      const cells = widgetCells(candidate);
      if (cells.every(cell => !occupied.has(cell))) {
        cells.forEach(cell => occupied.add(cell));
        resolved.set(widget.id, candidate);
        break;
      }
    }
  });

  return widgets.map(widget => resolved.get(widget.id) ?? widget);
};

const relocateAppsOutsideWidgets = (apps: AppItemType[], widgets: WidgetLayoutData[]) => {
  const occupied = new Set(widgets.flatMap(widgetCells));
  const highestWidgetCell = Math.max(-1, ...Array.from(occupied));
  const next = padToGrid(apps, Math.max(apps.length, highestWidgetCell + 1));
  const displaced = Array.from(occupied)
    .sort((a, b) => a - b)
    .map(cell => ({ cell, app: next[cell] }))
    .filter(({ app }) => app && !app.id.startsWith('__EMPTY__'));

  displaced.forEach(({ cell }) => { next[cell] = createEmptyApp(cell); });
  displaced.forEach(({ cell, app }) => {
    let destination = -1;
    for (let distance = 1; destination === -1; distance++) {
      const candidates = [cell + distance, cell - distance]
        .filter(index => index >= 0 && !occupied.has(index));
      destination = candidates.find(index =>
        index < next.length && next[index].id.startsWith('__EMPTY__')
      ) ?? -1;
      if (destination === -1 && cell + distance >= next.length) {
        const start = next.length;
        next.push(...padToGrid([], GRID_PAGE_SLOTS).map((_, index) => createEmptyApp(start + index)));
      }
    }
    next[destination] = app;
  });

  return next.map((app, index) => app.id.startsWith('__EMPTY__') ? createEmptyApp(index) : app);
};

const Home: React.FC = () => {
  useHomeHistoryBoundary();

  const [time, setTime] = useState('');
  const [isFolderOpen, setIsFolderOpen] = useState(false);
  const [showBetaNotice, setShowBetaNotice] = useState(false);
  const [showPreparing, setShowPreparing] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [showWidgetMenu, openWidgetMenu, closeWidgetMenu] = useHistoryBackedOverlay('widget-menu');
  const [showJournalApp, openJournalApp, closeJournalApp] = useHistoryBackedOverlay('journal-app');
  const [showFullScreenCalendar, setShowFullScreenCalendar] = useState(false);

  // Page transition fade overlay
  const [pageFading, setPageFading] = useState(false);
  const pendingHrefRef = useRef<string | null>(null);

  const navigateWithFade = useCallback((href: string) => {
    setPageFading(true);
    pendingHrefRef.current = href;
    setTimeout(() => {
      if (pendingHrefRef.current) {
        window.location.href = pendingHrefRef.current;
      }
    }, 320);
  }, []);

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setPageFading(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);
  const [activeWidgets, setActiveWidgets] = useState<WidgetLayoutData[]>(() =>
    padWidgetsToGrid(DEFAULT_WIDGET_LAYOUT)
  );
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [dragOverWidgetId, setDragOverWidgetId] = useState<string | null>(null);
  const [activeCalendarWidgetId, setActiveCalendarWidgetId] = useState<string | null>(null);
  const [journalSource, setJournalSource] = useState<'desktop' | 'dock' | 'folder' | 'journal-write' | 'journal-history' | 'journal-item' | null>(null);
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [betaUnread, setBetaUnread] = useState(() => {
    return localStorage.getItem(BETA_SEEN_KEY) !== LATEST_BETA_VERSION;
  });
  const [layoutReady, setLayoutReady] = useState(false);
  const skipNextLayoutSaveRef = React.useRef(false);

  useEffect(() => {
    if (showJournalApp) return;
    setJournalSource(null);
    setSelectedJournalId(null);
  }, [showJournalApp]);

  useEffect(() => {
    const hasSeen = localStorage.getItem('has_seen_beta_notice');
    if (!hasSeen) {
      setShowBetaNotice(true);
    }
  }, []);

  const handleCloseBetaNotice = () => {
    localStorage.setItem('has_seen_beta_notice', 'true');
    setShowBetaNotice(false);
  };

  React.useEffect(() => {
    (window as any).__neuritLogout = () => setConfirmLogout(true);
    const handleOpenJournal = (e: any) => {
      setJournalSource(e.detail?.source || 'desktop');
      setSelectedJournalId(e.detail?.journalId || null);
      openJournalApp();
    };
    window.addEventListener('open-journal', handleOpenJournal);

    return () => {
      delete (window as any).__neuritLogout;
      window.removeEventListener('open-journal', handleOpenJournal);
    };
  }, [openJournalApp]);

  const [mainApps, setMainApps] = useState<AppItemType[]>(createDefaultMainApps);
  const [folderApps, setFolderApps] = useState<AppItemType[]>(createDefaultFolderApps);

  useEffect(() => {
    // 홈 전체 배치는 btn_layout을 단일 원본으로 사용한다. 기존 로컬 값은
    // 위젯 데이터 마이그레이션에 한 번만 사용한 뒤 제거한다.
    let legacyWidgets: WidgetLayoutData[] | null = null;
    const legacyWidgetJson = localStorage.getItem('neurit_active_widgets');
    if (legacyWidgetJson) {
      try { legacyWidgets = padWidgetsToGrid(JSON.parse(legacyWidgetJson)); } catch { }
    }
    localStorage.removeItem('neurit_main_apps');
    localStorage.removeItem('neurit_folder_apps');
    localStorage.removeItem('neurit_layout_dirty');
    localStorage.removeItem('neurit_active_widgets');

    fetchBtnLayout().then(layout => {
      let hasServerWidgets = false;
      let hasCoordinateLayout = false;
      let loadedWidgets: WidgetLayoutData[] = [];
      if (layout && Array.isArray(layout) && layout.length > 0) {
        const widgetSepIdx = layout.indexOf(WIDGET_LAYOUT_SEPARATOR);
        const appLayout = widgetSepIdx === -1 ? layout : layout.slice(0, widgetSepIdx);
        if (widgetSepIdx !== -1 && layout[widgetSepIdx + 1]) {
          try {
            const parsedWidgets = JSON.parse(layout[widgetSepIdx + 1]);
            if (Array.isArray(parsedWidgets)) {
              const validWidgets = parsedWidgets.filter((widget: any) =>
                widget && typeof widget.id === 'string' && typeof widget.type === 'string' &&
                ['small', 'medium', 'large'].includes(widget.size)
              ) as WidgetLayoutData[];
              hasCoordinateLayout = validWidgets.every(isValidWidgetPosition);
              loadedWidgets = hasCoordinateLayout
                ? padWidgetsToGrid(validWidgets)
                : migrateWidgetsToCoordinates(padWidgetsToGrid(validWidgets));
              setActiveWidgets(loadedWidgets);
              hasServerWidgets = true;
            }
          } catch { }
        }

        let mainIds = appLayout;
        let folderIds: string[] = [];
        const sepIdx = appLayout.indexOf(DESKTOP_LAYOUT_SEPARATOR);
        if (sepIdx !== -1) {
          mainIds = appLayout.slice(0, sepIdx);
          folderIds = appLayout.slice(sepIdx + 1);
        }

        const mainFromServer = mainIds
          .map(id => ALL_APPS.find(a => a.id === id))
          .filter(Boolean) as AppItemType[];

        let folderFromServer: AppItemType[] = [];
        if (sepIdx !== -1) {
          folderFromServer = folderIds
            .map((id, index) => {
              if (id.startsWith('__EMPTY__')) return createEmptyApp(index);
              return ALL_APPS.find(a => a.id === id);
            })
            .filter(Boolean) as AppItemType[];

          const savedIds = new Set([...mainIds, ...folderIds]);
          const missingApps = ALL_APPS.filter(a => !savedIds.has(a.id));
          if (hasCoordinateLayout) {
            folderFromServer = padToGrid(folderFromServer);
            const widgetOccupied = new Set(loadedWidgets.flatMap(widgetCells));
            missingApps.forEach(app => {
              const emptyIndex = folderFromServer.findIndex((item, index) =>
                item.id.startsWith('__EMPTY__') && !widgetOccupied.has(index)
              );
              if (emptyIndex === -1) folderFromServer.push(app);
              else folderFromServer[emptyIndex] = app;
            });
          } else {
            folderFromServer = placeAppsAroundWidgets([...folderFromServer, ...missingApps], loadedWidgets);
          }
        } else {
          const savedIds = new Set(mainIds);
          folderFromServer = placeAppsAroundWidgets(ALL_APPS.filter(a => !savedIds.has(a.id)), loadedWidgets);
        }

        if (mainFromServer.length > 0) {
          setMainApps(mainFromServer);
          setFolderApps(folderFromServer);
        }
      }
      if (!hasServerWidgets) {
        if (!layout || layout.length === 0) {
          setMainApps(createDefaultMainApps());
          setActiveWidgets(DEFAULT_WIDGET_LAYOUT.map(widget => ({ ...widget })));
          setFolderApps(createDefaultFolderApps());
        } else {
          const migratedWidgets = migrateWidgetsToCoordinates(
            legacyWidgets ?? DEFAULT_WIDGET_LAYOUT
          );
          setActiveWidgets(migratedWidgets);
          setFolderApps(prev => placeAppsAroundWidgets(prev, migratedWidgets));
        }
      }
      // 이미 새 포맷이면 GET 직후 PUT하지 않는다. 구 포맷은 위젯까지 포함한
      // 새 btn_layout 포맷으로 한 번 자동 마이그레이션한다.
      skipNextLayoutSaveRef.current = hasCoordinateLayout;
      setLayoutReady(true);
    });
  }, []);

  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);
  const [toastMsg, setToastMsg] = useState('');
  const [holdProgress, setHoldProgress] = useState(0);
  const [holdingId, setHoldingId] = useState<string | null>(null);

  // ── Pointer-based drag state (works on touch + mouse) ──────────────────
  const pointerDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    dragging: boolean;
    sourceId: string;
    targetId: string | null;
    targetSlot: number | null;
    ghost: HTMLElement | null;
  } | null>(null);

  const exitEditMode = () => {
    setIsEditMode(false);
    setHoldProgress(0);
    setHoldingId(null);
  };

  const pressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressTimer = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const HOLD_DURATION = 800;
  const TICK_MS = 20;

  const handlePressStart = (id: string, isFolderApp: boolean) => {
    if (!layoutReady) return;
    // In edit mode tapping a folder app (on the folder modal) still moves it
    if (isEditMode && isFolderApp) {
      pressTimer.current = setTimeout(() => {
        handleMoveToMain(id);
      }, 600);
      return;
    }
    if (isEditMode) return; // already in edit mode, let pointer drag handle movement

    // Long-press to enter edit mode (for main dock + desktop apps)
    if (!isFolderApp) {
      let elapsed = 0;
      setHoldingId(id);
      setHoldProgress(0);

      progressTimer.current = setInterval(() => {
        elapsed += TICK_MS;
        const pct = Math.min((elapsed / HOLD_DURATION) * 100, 100);
        if (elapsed > 150) {
          setHoldProgress(pct);
        }
        if (elapsed >= HOLD_DURATION) {
          clearInterval(progressTimer.current!);
          progressTimer.current = null;
          setHoldProgress(0);
          setHoldingId(null);
          setIsEditMode(true);
          if (navigator.vibrate) navigator.vibrate([20, 10, 40]);
        }
      }, TICK_MS);
    } else {
      pressTimer.current = setTimeout(() => {
        handleMoveToMain(id);
      }, 600);
    }
  };

  const handlePressEnd = () => {
    if (progressTimer.current) {
      clearInterval(progressTimer.current);
      progressTimer.current = null;
      setHoldProgress(0);
      setHoldingId(null);
    }
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleMoveToFolder = (id: string) => {
    const app = mainApps.find(a => a.id === id);
    if (app) {
      setMainApps(prev => prev.filter(a => a.id !== id));
      setFolderApps(prev => {
        const newFolder = [...prev];
        const emptyIdx = newFolder.findIndex(a => a.id.startsWith('__EMPTY__'));
        if (emptyIdx !== -1) {
          newFolder[emptyIdx] = app;
        } else {
          newFolder.push(app);
        }
        return newFolder;
      });
    }
  };

  const handleMoveToMain = (id: string) => {
    const app = folderApps.find(a => a.id === id);
    if (app) {
      if (mainApps.length >= 3) {
        setToastMsg('더이상 추가할 수 없습니다');
        setTimeout(() => setToastMsg(''), 2000);
        return;
      }
      setFolderApps(prev => {
        const compacted = prev.filter(a => a.id !== id);
        compacted.push(createEmptyApp(compacted.length));
        return compacted;
      });
      setMainApps(prev => [...prev, app]);
    }
  };

  // ── Pointer-event drag (works on touch + mouse) ────────────────────────
  const performSwap = React.useCallback((dragId: string, targetId: string) => {
    if (!dragId || !targetId || dragId === targetId) return;
    if (targetId === 'dock-bg') {
      const folderDragIdx = folderApps.findIndex(a => a.id === dragId);
      if (folderDragIdx !== -1) {
        if (mainApps.length >= 4) {
          setToastMsg('더이상 추가할 수 없습니다');
          setTimeout(() => setToastMsg(''), 2000);
          return;
        }
        const newMain = [...mainApps];
        const newFolder = [...folderApps];
        const [item] = newFolder.splice(folderDragIdx, 1);
        newFolder.push(createEmptyApp(newFolder.length));
        newMain.push(item);
        setMainApps(newMain);
        setFolderApps(newFolder);
      }
      return;
    }

    const mainDragIdx = mainApps.findIndex(a => a.id === dragId);
    const folderDragIdx = folderApps.findIndex(a => a.id === dragId);
    const mainTargetIdx = mainApps.findIndex(a => a.id === targetId);
    const folderTargetIdx = folderApps.findIndex(a => a.id === targetId);

    if (mainDragIdx !== -1 && mainTargetIdx !== -1) {
      const newApps = [...mainApps];
      const [item] = newApps.splice(mainDragIdx, 1);
      newApps.splice(mainTargetIdx, 0, item);
      setMainApps(newApps);
    } else if (folderDragIdx !== -1 && folderTargetIdx !== -1) {
      const newApps = [...folderApps];
      const temp = newApps[folderDragIdx];
      newApps[folderDragIdx] = newApps[folderTargetIdx];
      newApps[folderTargetIdx] = temp;
      if (newApps[folderDragIdx].id.startsWith('__EMPTY__')) newApps[folderDragIdx].id = `__EMPTY__${folderDragIdx}`;
      if (newApps[folderTargetIdx].id.startsWith('__EMPTY__')) newApps[folderTargetIdx].id = `__EMPTY__${folderTargetIdx}`;
      setFolderApps(newApps);
    } else if (mainDragIdx !== -1 && folderTargetIdx !== -1) {
      const newMain = [...mainApps];
      const newFolder = [...folderApps];
      const [item] = newMain.splice(mainDragIdx, 1);
      if (newFolder[folderTargetIdx].id.startsWith('__EMPTY__')) {
        newFolder[folderTargetIdx] = item;
      } else {
        const existingApp = newFolder[folderTargetIdx];
        newFolder[folderTargetIdx] = item;
        newMain.splice(mainDragIdx, 0, existingApp);
      }
      setMainApps(newMain);
      setFolderApps(newFolder);
    } else if (folderDragIdx !== -1 && mainTargetIdx !== -1) {
      if (mainApps.length >= 4) {
        setToastMsg('더이상 추가할 수 없습니다');
        setTimeout(() => setToastMsg(''), 2000);
        return;
      }
      const newMain = [...mainApps];
      const newFolder = [...folderApps];
      const [item] = newFolder.splice(folderDragIdx, 1);
      newFolder.push(createEmptyApp(newFolder.length));
      newMain.splice(mainTargetIdx, 0, item);
      setMainApps(newMain);
      setFolderApps(newFolder);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainApps, folderApps]);

  const moveAppToSlot = React.useCallback((sourceId: string, targetSlot: number) => {
    const sourceSlot = folderApps.findIndex(app => app.id === sourceId);
    if (sourceSlot === -1 || sourceSlot === targetSlot || targetSlot < 0) return;
    const widgetOccupied = new Set(activeWidgets.flatMap(widgetCells));
    if (widgetOccupied.has(targetSlot)) return;

    const totalSlots = Math.max(
      folderApps.length,
      (Math.floor(targetSlot / GRID_PAGE_SLOTS) + 1) * GRID_PAGE_SLOTS
    );
    const next = padToGrid(folderApps, totalSlots);
    const availableSlots = Array.from({ length: totalSlots }, (_, index) => index)
      .filter(index => !widgetOccupied.has(index));
    const sourceOrder = availableSlots.indexOf(sourceSlot);
    const targetOrder = availableSlots.indexOf(targetSlot);
    if (sourceOrder === -1 || targetOrder === -1) return;

    const dragged = next[sourceSlot];
    if (next[targetSlot].id.startsWith('__EMPTY__')) {
      next[sourceSlot] = createEmptyApp(sourceSlot);
      next[targetSlot] = dragged;
    } else if (sourceOrder < targetOrder) {
      for (let i = sourceOrder; i < targetOrder; i++) {
        const from = availableSlots[i + 1];
        next[availableSlots[i]] = next[from];
      }
      next[targetSlot] = dragged;
    } else {
      for (let i = sourceOrder; i > targetOrder; i--) {
        const from = availableSlots[i - 1];
        next[availableSlots[i]] = next[from];
      }
      next[targetSlot] = dragged;
    }
    setFolderApps(next.map((app, index) => app.id.startsWith('__EMPTY__') ? createEmptyApp(index) : app));
  }, [activeWidgets, folderApps]);

  const moveMainAppToSlot = React.useCallback((sourceId: string, targetSlot: number) => {
    const sourceIndex = mainApps.findIndex(app => app.id === sourceId);
    if (sourceIndex === -1 || activeWidgets.flatMap(widgetCells).includes(targetSlot)) return;
    const nextMain = [...mainApps];
    const [dragged] = nextMain.splice(sourceIndex, 1);
    const nextFolder = padToGrid(folderApps, (Math.floor(targetSlot / GRID_PAGE_SLOTS) + 1) * GRID_PAGE_SLOTS);
    const displaced = nextFolder[targetSlot];
    nextFolder[targetSlot] = dragged;
    if (displaced && !displaced.id.startsWith('__EMPTY__')) nextMain.splice(sourceIndex, 0, displaced);
    setMainApps(nextMain);
    setFolderApps(nextFolder.map((app, index) => app.id.startsWith('__EMPTY__') ? createEmptyApp(index) : app));
  }, [activeWidgets, folderApps, mainApps]);

  const gridSlotFromPoint = (clientX: number, clientY: number) => {
    const cell = document.elementsFromPoint(clientX, clientY)
      .find(element => (element as HTMLElement).dataset.gridSlot != null) as HTMLElement | undefined;
    if (!cell) return null;
    const slot = Number(cell.dataset.gridSlot);
    return Number.isInteger(slot) ? slot : null;
  };

  // Pointer drag handlers
  const handlePointerDown = (e: React.PointerEvent, id: string) => {
    if (!layoutReady || !isEditMode) return;
    if (id.startsWith('__EMPTY__')) return;
    clearEdgeAutoScroll();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
      sourceId: id,
      targetId: null,
      targetSlot: null,
      ghost: null,
    };
    setDraggedId(id);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!pointerDragRef.current) return;
    const dx = e.clientX - pointerDragRef.current.startX;
    const dy = e.clientY - pointerDragRef.current.startY;
    if (!pointerDragRef.current.dragging && Math.hypot(dx, dy) > 6) {
      pointerDragRef.current.dragging = true;
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const ghost = el.cloneNode(true) as HTMLElement;
      ghost.style.position = 'fixed';
      ghost.style.top = `${rect.top}px`;
      ghost.style.left = `${rect.left}px`;
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${rect.height}px`;
      ghost.style.opacity = '0.8';
      ghost.style.pointerEvents = 'none';
      ghost.style.zIndex = '999999';
      ghost.style.margin = '0';
      ghost.style.transform = `translate(${dx}px, ${dy}px)`;
      document.body.appendChild(ghost);
      pointerDragRef.current.ghost = ghost;
    }

    if (!pointerDragRef.current.dragging) return;

    if (pointerDragRef.current.ghost) {
      pointerDragRef.current.ghost.style.transform = `translate(${dx}px, ${dy}px)`;
    }

    checkEdgeAutoScroll(e.clientX);

    // Hit-test what element is under the pointer (ghost already has pointer-events:none, so it's transparent to elementFromPoint)
    const hitEl = document.elementFromPoint(e.clientX, e.clientY);

    const appEl = hitEl?.closest('[data-app-id]') as HTMLElement | null;
    const targetId = appEl?.dataset.appId ?? null;
    const targetSlot = gridSlotFromPoint(e.clientX, e.clientY);
    pointerDragRef.current.targetSlot = targetSlot;
    setDragOverSlot(targetSlot);
    if (targetId && targetId !== pointerDragRef.current.sourceId) {
      pointerDragRef.current.targetId = targetId;
      setDragOverId(targetId);
    } else {
      setDragOverId(null);
    }
  };

  const finishAppPointerDrag = (clientX: number, clientY: number, commit: boolean) => {
    if (!pointerDragRef.current) return;
    const { dragging, sourceId, targetId: lastTargetId, targetSlot: lastTargetSlot, ghost } = pointerDragRef.current;
    pointerDragRef.current = null;
    if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
    if (commit && dragging) {
      const targetSlot = gridSlotFromPoint(clientX, clientY) ?? lastTargetSlot;
      const hitEl = document.elementFromPoint(clientX, clientY);
      const appEl = hitEl?.closest('[data-app-id]') as HTMLElement | null;
      let targetId = appEl?.dataset.appId ?? lastTargetId;

      // 페이지 가장자리 자동 전환 뒤 빈 배경에 놓아도 해당 페이지로 이동시킨다.
      // 빈 슬롯이 없으면 그 페이지의 마지막 실제 앱과 교환한다.
      if (!targetId) {
        const pageEl = hitEl?.closest('[data-page-index]') as HTMLElement | null;
        const pageIndex = Number(pageEl?.dataset.pageIndex);
        const destinationApps = Number.isInteger(pageIndex) ? pages[pageIndex]?.apps ?? [] : [];
        const emptyTarget = destinationApps.find(({ app }) => app.id.startsWith('__EMPTY__'));
        const lastRealTarget = [...destinationApps]
          .reverse()
          .find(({ app }) => !app.id.startsWith('__EMPTY__') && app.id !== sourceId);
        targetId = emptyTarget?.app.id ?? lastRealTarget?.app.id ?? null;
      }

      if (folderApps.some(app => app.id === sourceId) && targetSlot != null) {
        moveAppToSlot(sourceId, targetSlot);
      } else if (mainApps.some(app => app.id === sourceId) && targetSlot != null) {
        moveMainAppToSlot(sourceId, targetSlot);
      } else if (targetId && targetId !== sourceId) {
        performSwap(sourceId, targetId);
      }
    }
    clearEdgeAutoScroll();
    setDraggedId(null);
    setDragOverId(null);
    setDragOverSlot(null);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    finishAppPointerDrag(e.clientX, e.clientY, true);
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    finishAppPointerDrag(e.clientX, e.clientY, false);
  };

  // Widget pointer drag
  const widgetPointerDragRef = React.useRef<{
    pointerId: number; sourceId: string; dragging: boolean; startX: number; startY: number;
    targetSlot: number | null; ghost: HTMLElement | null;
  } | null>(null);

  const handleWidgetPointerDown = (e: React.PointerEvent, id: string) => {
    if (!isEditMode || id.startsWith('__EMPTY__')) return;
    clearEdgeAutoScroll();
    e.currentTarget.setPointerCapture(e.pointerId);
    widgetPointerDragRef.current = { pointerId: e.pointerId, sourceId: id, dragging: false, startX: e.clientX, startY: e.clientY, targetSlot: null, ghost: null };
    setDraggedWidgetId(id);
  };

  const handleWidgetPointerMove = (e: React.PointerEvent) => {
    if (!widgetPointerDragRef.current) return;
    const dx = e.clientX - widgetPointerDragRef.current.startX;
    const dy = e.clientY - widgetPointerDragRef.current.startY;
    if (!widgetPointerDragRef.current.dragging && Math.hypot(dx, dy) > 6) {
      widgetPointerDragRef.current.dragging = true;
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const ghost = el.cloneNode(true) as HTMLElement;
      ghost.style.position = 'fixed';
      ghost.style.top = `${rect.top}px`;
      ghost.style.left = `${rect.left}px`;
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${rect.height}px`;
      ghost.style.opacity = '0.8';
      ghost.style.pointerEvents = 'none';
      ghost.style.zIndex = '999999';
      ghost.style.margin = '0';
      ghost.style.transform = `translate(${dx}px, ${dy}px)`;
      document.body.appendChild(ghost);
      widgetPointerDragRef.current.ghost = ghost;
    }

    if (!widgetPointerDragRef.current.dragging) return;

    if (widgetPointerDragRef.current.ghost) {
      widgetPointerDragRef.current.ghost.style.transform = `translate(${dx}px, ${dy}px)`;
    }

    checkEdgeAutoScroll(e.clientX);

    const hitEl = document.elementFromPoint(e.clientX, e.clientY);
    const targetSlot = gridSlotFromPoint(e.clientX, e.clientY);
    widgetPointerDragRef.current.targetSlot = targetSlot;
    setDragOverSlot(targetSlot);

    const wEl = hitEl?.closest('[data-widget-id]') as HTMLElement | null;
    const targetId = wEl?.dataset.widgetId ?? null;
    if (targetId && targetId !== widgetPointerDragRef.current.sourceId) {
      setDragOverWidgetId(targetId);
    } else {
      setDragOverWidgetId(null);
    }
  };

  const finishWidgetPointerDrag = (clientX: number, clientY: number, commit: boolean) => {
    if (!widgetPointerDragRef.current) return;
    const { dragging, sourceId, targetSlot: lastTargetSlot, ghost } = widgetPointerDragRef.current;
    widgetPointerDragRef.current = null;
    if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
    if (commit && dragging) {
      const targetSlot = gridSlotFromPoint(clientX, clientY) ?? lastTargetSlot;
      const hitEl = document.elementFromPoint(clientX, clientY);
      const wEl = hitEl?.closest('[data-widget-id]') as HTMLElement | null;
      const targetId = wEl?.dataset.widgetId ?? null;
      if (targetSlot != null) {
        const sourceWidget = activeWidgets.find(widget => widget.id === sourceId);
        if (sourceWidget) {
          const page = Math.floor(targetSlot / GRID_PAGE_SLOTS);
          const pageSlot = targetSlot % GRID_PAGE_SLOTS;
          const { width, height } = widgetDimensions(sourceWidget.size);
          const row = Math.min(Math.floor(pageSlot / GRID_COLS), GRID_ROWS - height);
          const col = Math.min(pageSlot % GRID_COLS, GRID_COLS - width);
          const movedWidget = { ...sourceWidget, page, row, col };
          const otherWidgets = activeWidgets.filter(widget => widget.id !== sourceId);
          const otherOccupied = new Set(otherWidgets.flatMap(widgetCells));
          const movedCells = widgetCells(movedWidget);

          if (movedCells.every(cell => !otherOccupied.has(cell))) {
            const widgetOccupied = new Set([...Array.from(otherOccupied), ...movedCells]);
            setActiveWidgets(prev => prev.map(widget => widget.id === sourceId ? movedWidget : widget));
            setFolderApps(prev => {
              const next = padToGrid(prev, Math.max(prev.length, (page + 1) * GRID_PAGE_SLOTS));
              const displaced = movedCells
                .map(cell => ({ cell, app: next[cell] }))
                .filter(({ app }) => app && !app.id.startsWith('__EMPTY__'));
              displaced.forEach(({ cell }) => { next[cell] = createEmptyApp(cell); });
              displaced.forEach(({ cell, app }) => {
                let destination = -1;
                let distance = 1;
                while (destination === -1) {
                  const candidates = [cell + distance, cell - distance]
                    .filter(index => index >= 0 && !widgetOccupied.has(index));
                  destination = candidates.find(index =>
                    index < next.length && next[index].id.startsWith('__EMPTY__')
                  ) ?? -1;
                  if (destination === -1 && cell + distance >= next.length) {
                    const start = next.length;
                    next.push(...padToGrid([], GRID_PAGE_SLOTS).map((_, index) => createEmptyApp(start + index)));
                  }
                  distance++;
                }
                next[destination] = app;
              });
              return next.map((app, index) => app.id.startsWith('__EMPTY__') ? createEmptyApp(index) : app);
            });
          }
        }
      } else if (targetId && targetId !== sourceId) {
        const newWidgets = [...activeWidgets];
        const srcIdx = newWidgets.findIndex(x => x.id === sourceId);
        const destIdx = newWidgets.findIndex(x => x.id === targetId);
        if (srcIdx !== -1 && destIdx !== -1) {
          const temp = newWidgets[srcIdx];
          newWidgets[srcIdx] = newWidgets[destIdx];
          newWidgets[destIdx] = temp;
          if (newWidgets[srcIdx].id.startsWith('__EMPTY__')) newWidgets[srcIdx].id = `__EMPTY__WIDGET_${Date.now()}_1`;
          if (newWidgets[destIdx].id.startsWith('__EMPTY__')) newWidgets[destIdx].id = `__EMPTY__WIDGET_${Date.now()}_2`;
          setActiveWidgets(newWidgets);
        }
      }
    }
    clearEdgeAutoScroll();
    setDraggedWidgetId(null);
    setDragOverWidgetId(null);
    setDragOverSlot(null);
  };

  const handleWidgetPointerUp = (e: React.PointerEvent) => {
    finishWidgetPointerDrag(e.clientX, e.clientY, true);
  };

  const handleWidgetPointerCancel = (e: React.PointerEvent) => {
    finishWidgetPointerDrag(e.clientX, e.clientY, false);
  };

  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!layoutReady) return;
    if (skipNextLayoutSaveRef.current) {
      skipNextLayoutSaveRef.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const seenButtonIds = new Set<string>();
      const mainLayout = mainApps.flatMap(app => {
        if (seenButtonIds.has(app.id)) return [];
        seenButtonIds.add(app.id);
        return [app.id];
      });
      const folderLayout = folderApps.map((app, index) => {
        if (app.id.startsWith('__EMPTY__')) return `__EMPTY__${index}`;
        if (seenButtonIds.has(app.id)) return `__EMPTY__DUPLICATE_${index}`;
        seenButtonIds.add(app.id);
        return app.id;
      });
      const combinedLayout = [
        ...mainLayout,
        DESKTOP_LAYOUT_SEPARATOR,
        ...folderLayout,
        WIDGET_LAYOUT_SEPARATOR,
        JSON.stringify(activeWidgets.map(({ id, type, size, page, row, col }) => ({ id, type, size, page, row, col })))
      ];
      const hasDuplicates = new Set(combinedLayout).size !== combinedLayout.length;
      if (hasDuplicates) {
        console.error('btn_layout serialization produced duplicate entries', combinedLayout);
        return;
      }
      await saveBtnLayout(combinedLayout);
    }, 250);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [mainApps, folderApps, activeWidgets, layoutReady]);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      let h = now.getHours();
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      const m = now.getMinutes().toString().padStart(2, '0');
      setTime(`${h}:${m} ${ampm}`);
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, []);

  const handleRemoveWidget = (id: string) => {
    setActiveWidgets(prev => prev.filter(w => w.id !== id));
  };

  const handleAddWidget = (type: string, defaultSize: WidgetSize) => {
    const { width, height } = widgetDimensions(defaultSize);
    const occupied = new Set(activeWidgets.flatMap(widgetCells));
    folderApps.forEach((app, index) => {
      if (!app.id.startsWith('__EMPTY__')) occupied.add(index);
    });
    let position = { page: 0, row: 0, col: 0 };
    let found = false;
    for (let page = 0; !found; page++) {
      for (let row = 0; row <= GRID_ROWS - height && !found; row++) {
        for (let col = 0; col <= GRID_COLS - width; col++) {
          const cells = widgetCells({ id: 'candidate', type, size: defaultSize, page, row, col });
          if (cells.every(cell => !occupied.has(cell))) {
            position = { page, row, col };
            found = true;
            break;
          }
        }
      }
    }
    const newWidget: WidgetLayoutData = {
      id: `${type}-${Date.now()}`,
      type,
      size: defaultSize,
      ...position,
    };
    setActiveWidgets(prev => [...prev, newWidget]);
    closeWidgetMenu();
  };

  const handleResizeWidget = (id: string) => {
    const sizes: WidgetSize[] = ['small', 'medium', 'large'];
    const resizedWidgets = activeWidgets.map(widget => {
      if (widget.id !== id) return widget;
      const nextSize = sizes[(sizes.indexOf(widget.size) + 1) % sizes.length];
      const { width, height } = widgetDimensions(nextSize);
      return {
        ...widget,
        size: nextSize,
        row: Math.min(widget.row ?? 0, GRID_ROWS - height),
        col: Math.min(widget.col ?? 0, GRID_COLS - width),
      };
    });
    const collisionFreeWidgets = resolveWidgetCollisions(resizedWidgets, id);
    setActiveWidgets(collisionFreeWidgets);
    setFolderApps(prev => relocateAppsOutsideWidgets(prev, collisionFreeWidgets));
  };

  const pages = useMemo(() => {
    const highestAppSlot = folderApps.reduce((highest, app, index) =>
      app.id.startsWith('__EMPTY__') ? highest : index, -1);
    const contentPages = Math.max(
      1,
      Math.floor(highestAppSlot / GRID_PAGE_SLOTS) + 1,
      ...activeWidgets.map(widget => (widget.page ?? 0) + 1)
    );
    const pageCount = isEditMode ? Math.max(MIN_DESKTOP_PAGES, contentPages + 1) : contentPages;
    const paddedApps = padToGrid(folderApps, pageCount * GRID_PAGE_SLOTS);

    return Array.from({ length: pageCount }, (_, pageIndex) => ({
      id: `page-${pageIndex}`,
      widgets: activeWidgets.filter(widget => (widget.page ?? 0) === pageIndex),
      apps: paddedApps
        .slice(pageIndex * GRID_PAGE_SLOTS, (pageIndex + 1) * GRID_PAGE_SLOTS)
        .map((app, pageSlot) => ({ app, slot: pageIndex * GRID_PAGE_SLOTS + pageSlot }))
    }));
  }, [activeWidgets, folderApps, isEditMode]);

  const currentPageRef = React.useRef(0);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const page = Math.round(el.scrollLeft / el.clientWidth);
    currentPageRef.current = page;
    setCurrentPage(page);
  };

  const scrollAreaRef = React.useRef<HTMLDivElement>(null);

  const scrollToPage = useCallback((index: number) => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(index, pages.length - 1));
    currentPageRef.current = clamped;
    el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    setCurrentPage(clamped);
  }, [pages.length]);

  // 뒤쪽 빈 페이지가 사라지는 경우, 현재 보고 있던 페이지가 없어졌다면 마지막 페이지로 보정
  useEffect(() => {
    if (currentPage > pages.length - 1) {
      scrollToPage(pages.length - 1);
    }
  }, [pages.length, currentPage, scrollToPage]);

  // 마우스 휠/트랙패드는 연속 픽셀 이동 대신 한 페이지씩 확실하게 전환한다.
  const wheelLockedRef = React.useRef(false);
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (pages.length <= 1) return;
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (Math.abs(delta) < 8 || wheelLockedRef.current) return;
    e.preventDefault();
    wheelLockedRef.current = true;
    scrollToPage(currentPageRef.current + (delta > 0 ? 1 : -1));
    window.setTimeout(() => { wheelLockedRef.current = false; }, 380);
  };

  // PC 마우스와 태블릿 터치를 같은 포인터 기반 스와이프로 처리한다.
  const panRef = React.useRef<{
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    moved: boolean;
  } | null>(null);
  const suppressPageClickRef = React.useRef(false);

  const handleAreaPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerDragRef.current || widgetPointerDragRef.current) return;
    const target = e.target as HTMLElement;
    const draggable = target.closest('[data-app-id], [data-widget-id]') as HTMLElement | null;
    const draggableId = draggable?.dataset.appId ?? draggable?.dataset.widgetId ?? '';
    if (isEditMode && draggableId && !draggableId.startsWith('__EMPTY__')) return;

    const el = e.currentTarget;
    panRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      moved: false
    };
  };

  const handleAreaPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!panRef.current || panRef.current.pointerId !== e.pointerId) return;
    const distance = e.clientX - panRef.current.startX;
    if (Math.abs(distance) > 5) panRef.current.moved = true;
    if (!panRef.current.moved) return;
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    handlePressEnd();
    e.preventDefault();
    e.currentTarget.scrollLeft = panRef.current.startScrollLeft - distance;
  };

  const endAreaPan = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!panRef.current || panRef.current.pointerId !== e.pointerId) return;
    const { startX, startScrollLeft, moved } = panRef.current;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    panRef.current = null;
    handlePressEnd();
    if (!moved) return;

    suppressPageClickRef.current = true;
    window.setTimeout(() => { suppressPageClickRef.current = false; }, 0);

    const distance = e.clientX - startX;
    const pageWidth = e.currentTarget.clientWidth;
    const startPage = Math.round(startScrollLeft / pageWidth);
    const targetPage = Math.abs(distance) >= Math.min(80, pageWidth * 0.12)
      ? startPage + (distance < 0 ? 1 : -1)
      : Math.round(e.currentTarget.scrollLeft / pageWidth);
    scrollToPage(targetPage);
  };

  // 드래그 중 화면 가장자리 근처에 머물면 옆 페이지로 자동 이동 (예: 2페이지 → 1페이지로 앱 옮기기)
  const edgeScrollTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const edgeScrollDirRef = React.useRef<'left' | 'right' | null>(null);

  const clearEdgeAutoScroll = () => {
    if (edgeScrollTimerRef.current) {
      clearTimeout(edgeScrollTimerRef.current);
      edgeScrollTimerRef.current = null;
    }
    edgeScrollDirRef.current = null;
  };

  const checkEdgeAutoScroll = (clientX: number) => {
    const el = scrollAreaRef.current;
    if (!el || pages.length <= 1) return;
    const rect = el.getBoundingClientRect();
    const EDGE = 24; // 페이지 안에서 평범하게 재배치할 때는 걸리지 않도록 화면 진짜 가장자리 근처로만 좁힘
    let dir: 'left' | 'right' | null = null;
    const pageAtCheck = currentPageRef.current;
    if (clientX - rect.left < EDGE && pageAtCheck > 0) dir = 'left';
    else if (rect.right - clientX < EDGE && pageAtCheck < pages.length - 1) dir = 'right';

    if (dir === edgeScrollDirRef.current) return;
    clearEdgeAutoScroll();
    if (dir) {
      edgeScrollDirRef.current = dir;
      edgeScrollTimerRef.current = setTimeout(() => {
        scrollToPage(currentPageRef.current + (dir === 'left' ? -1 : 1));
        edgeScrollDirRef.current = null;
      }, 550);
    }
  };

  // 페이지 자동 전환으로 원래 드래그 요소가 화면 밖으로 밀려나도 종료 이벤트를
  // window에서 확정한다. 그렇지 않으면 draggedId가 남아 앱 전체 입력이 잠긴다.
  useEffect(() => {
    const handleWindowPointerUp = (event: PointerEvent) => {
      if (pointerDragRef.current?.pointerId === event.pointerId) {
        finishAppPointerDrag(event.clientX, event.clientY, true);
      }
      if (widgetPointerDragRef.current?.pointerId === event.pointerId) {
        finishWidgetPointerDrag(event.clientX, event.clientY, true);
      }
    };
    const handleWindowPointerCancel = (event: PointerEvent) => {
      if (pointerDragRef.current?.pointerId === event.pointerId) {
        finishAppPointerDrag(event.clientX, event.clientY, false);
      }
      if (widgetPointerDragRef.current?.pointerId === event.pointerId) {
        finishWidgetPointerDrag(event.clientX, event.clientY, false);
      }
    };

    window.addEventListener('pointerup', handleWindowPointerUp, true);
    window.addEventListener('pointercancel', handleWindowPointerCancel, true);
    return () => {
      window.removeEventListener('pointerup', handleWindowPointerUp, true);
      window.removeEventListener('pointercancel', handleWindowPointerCancel, true);
    };
  });

  return (
    <div onClick={() => exitEditMode()} style={{ width: '100%', height: '100%' }}>
      <GlobalStyle />
      <ResponsiveShell>
        <BackgroundWallpaper />

        <StatusBar style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <StatusSignal>
              <Bar $h={5} /><Bar $h={8} /><Bar $h={11} /><Bar $h={14} />
            </StatusSignal>
          </div>
          <StatusTime style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>{time}</StatusTime>
          <StatusLocation>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {t.home.location}
          </StatusLocation>
        </StatusBar>

        <TabletDesktopArea
          ref={scrollAreaRef}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onPointerDownCapture={handleAreaPointerDown}
          onPointerMove={handleAreaPointerMove}
          onPointerUp={endAreaPan}
          onPointerCancel={endAreaPan}
        >
          {pages.map((page, pageIndex) => (
            <PageContainer key={page.id} data-page-index={pageIndex}>
              {(page.widgets.length > 0 || page.apps.length > 0) && (
                <DesktopPageGrid data-desktop-grid>
                  {Array.from({ length: GRID_PAGE_SLOTS }, (_, pageSlot) => {
                    const absoluteSlot = pageIndex * GRID_PAGE_SLOTS + pageSlot;
                    const isHover = dragOverSlot === absoluteSlot;
                    return (
                      <GridDropCell
                        key={`drop-${absoluteSlot}`}
                        data-grid-slot={absoluteSlot}
                        style={{
                          gridColumn: (pageSlot % GRID_COLS) + 1,
                          gridRow: Math.floor(pageSlot / GRID_COLS) + 1,
                          background: isEditMode && isHover ? 'rgba(101, 163, 255, 0.12)' : 'transparent',
                          boxShadow: isEditMode && isHover ? 'inset 0 0 0 2px rgba(101, 163, 255, 0.45)' : 'none',
                        }}
                      />
                    );
                  })}
                  {page.widgets.map(w => {
                    const isEmpty = w.id.startsWith('__EMPTY__');
                    // 빈 슬롯은 편집 여부와 관계없이 그리드에 렌더링하지 않아 위젯 영역이 항상 실제 위젯 크기에 맞춰짐
                    if (isEmpty) return null;
                    // Grid span: small=2col×2row, medium=4col×2row, large=4col×4row
                    const colSpan = w.size === 'small' ? 2 : 4;
                    const rowSpan = w.size === 'large' ? 4 : 2;
                    return (
                      <motion.div
                        key={w.id}
                        layout
                        transition={{
                          layout: { type: 'spring', stiffness: 360, damping: 34, mass: 0.8 },
                          opacity: { duration: 0.15 },
                          scale: { duration: 0.15 },
                        }}
                        animate={{
                          opacity: draggedWidgetId === w.id ? 0.4 : 1,
                          scale: dragOverWidgetId === w.id && !isEmpty ? 1.02 : 1,
                        }}
                        data-widget-id={w.id}
                        onPointerDown={(e) => { if (!isEmpty) { e.stopPropagation(); handleWidgetPointerDown(e, w.id); if (!isEditMode) handlePressStart(w.id, false); } }}
                        onPointerMove={handleWidgetPointerMove}
                        onPointerUp={(e) => { if (!isEmpty) { handleWidgetPointerUp(e); handlePressEnd(); } }}
                        onPointerCancel={(e) => { if (!isEmpty) { handleWidgetPointerCancel(e); handlePressEnd(); } }}
                        onClick={(e) => {
                          if (suppressPageClickRef.current) { e.preventDefault(); e.stopPropagation(); return; }
                          if (isEmpty) return;
                          if (isEditMode) { e.stopPropagation(); return; }
                          if (w.type === 'date') setActiveCalendarWidgetId(w.id);
                        }}
                        style={{
                          gridColumn: `${(w.col ?? 0) + 1} / span ${colSpan}`,
                          gridRow: `${(w.row ?? 0) + 1} / span ${rowSpan}`,
                          borderRadius: '24px',
                          pointerEvents: (isEmpty && !isEditMode) ? 'none' : 'auto',
                          touchAction: isEditMode ? 'none' : 'auto',
                          zIndex: 2,
                          minHeight: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {!isEmpty && (
                          <WidgetContainer
                            data={w}
                            isEditMode={isEditMode}
                            isDragged={draggedWidgetId === w.id}
                            onRemove={handleRemoveWidget}
                            onResize={handleResizeWidget}
                            onWidgetClick={(type, itemId) => {
                              if (type === 'date') setActiveCalendarWidgetId(w.id);
                              if (type === 'journal-write') {
                                setJournalSource('journal-write');
                                openJournalApp();
                              }
                              if (type === 'journal-history') {
                                setJournalSource('journal-history');
                                setSelectedJournalId(null);
                                openJournalApp();
                              }
                              if (type === 'delivery-tracking') {
                                window.location.href = itemId
                                  ? `/archive?mail_id=${encodeURIComponent(itemId)}`
                                  : '/letterbox?tab=transit';
                              }
                            }}
                          />
                        )}
                      </motion.div>
                    )
                  })}
                  {page.apps.map(({ app, slot }, i) => {
                    const isEmpty = app.id.startsWith('__EMPTY__');
                    const pageSlot = slot % GRID_PAGE_SLOTS;
                    const isCoveredByWidget = activeWidgets.some(widget => widgetCells(widget).includes(slot));
                    if (isCoveredByWidget) return null;
                    // 빈 슬롯은 편집 중 드롭 대상으로만 필요하다. 평상시에는 DOM에서
                    // 제외해 같은 페이지의 실제 앱들이 빈칸 없이 앞으로 채워지게 한다.
                    if (isEmpty && !isEditMode) return null;
                    return (
                      <AppItem
                        layout
                        key={app.id}
                        data-app-id={app.id}
                        $isDragged={draggedId === app.id}
                        $isDragInProgress={!!draggedId}
                        onPointerDown={(e) => {
                          if (isEmpty) return;
                          e.stopPropagation();
                          handlePressStart(app.id, false);
                          handlePointerDown(e, app.id);
                        }}
                        onPointerMove={handlePointerMove}
                        onPointerUp={(e) => {
                          if (isEmpty) return;
                          handlePointerUp(e);
                          handlePressEnd();
                        }}
                        onPointerCancel={(e) => { handlePointerCancel(e); handlePressEnd(); }}
                        onClick={(e) => {
                          if (suppressPageClickRef.current) { e.preventDefault(); e.stopPropagation(); return; }
                          if (isEmpty) return;
                          if (isEditMode) { e.stopPropagation(); return; }
                          if (app.id === 'decorateMailbox') {
                            setShowPreparing(true);
                          } else if (app.id === 'beta_history') {
                            localStorage.setItem(BETA_SEEN_KEY, LATEST_BETA_VERSION);
                            setBetaUnread(false);
                            navigateWithFade('/beta');
                          } else if (app.id === 'widget_settings') {
                            openWidgetMenu();
                          } else if (app.id === 'journal') {
                            openJournalApp();
                          } else if (app.action) {
                            app.action();
                          } else {
                            navigateWithFade(app.href || '#');
                          }
                        }}
                        style={{
                          gridColumn: (pageSlot % GRID_COLS) + 1,
                          gridRow: Math.floor(pageSlot / GRID_COLS) + 1,
                          ...(isEmpty && dragOverId !== app.id ? { opacity: 0 } : {}),
                          pointerEvents: (isEmpty && !isEditMode) ? 'none' : 'auto',
                          cursor: isEmpty ? 'default' : 'pointer',
                          touchAction: isEditMode ? 'none' : 'auto',
                          zIndex: 1,
                        }}
                      >
                        <AppItemContent $delay={isEmpty ? 0 : i} $isEditMode={isEditMode && !isEmpty}>
                          <AppIcon $color={app.color} $isEditMode={isEditMode && !isEmpty}
                            style={{
                              ...(holdingId === app.id ? { animation: 'none' } : {}),
                              ...(isEmpty && dragOverId === app.id ? { background: 'rgba(0,0,0,0.05)', border: '2px dashed #B0A080' } : {}),
                              ...(!isEmpty && dragOverId === app.id ? { transform: 'scale(1.1)', boxShadow: '0 0 12px rgba(101,163,255,0.6)' } : {})
                            }}
                          >
                            {!isEmpty && app.icon}
                            {!isEmpty && app.id === 'beta_history' && betaUnread && <UnreadDot />}

                            {isEditMode && !isEmpty && (
                              <BadgeBtn onClick={(e) => { e.stopPropagation(); handleMoveToMain(app.id); }}>
                                +
                              </BadgeBtn>
                            )}
                          </AppIcon>
                          <AppName style={isEmpty ? { opacity: 0 } : {}}>{app.name}</AppName>
                        </AppItemContent>
                      </AppItem>
                    )
                  })}
                </DesktopPageGrid>
              )}
            </PageContainer>
          ))}
        </TabletDesktopArea>

        {pages.length > 1 && (
          <PageIndicatorContainer $isEditMode={isEditMode}>
            {pages.map((_, i) => (
              <Dot key={i} $active={currentPage === i} onClick={(e) => { e.stopPropagation(); scrollToPage(i); }} />
            ))}
          </PageIndicatorContainer>
        )}

        <DockArea
          data-app-id="dock-bg"
          style={dragOverId === 'dock-bg' ? { boxShadow: '0 0 0 4px rgba(101,163,255,0.5)' } : {}}
        >
          <style>{`.dock-app-name { display: none !important; }`}</style>
          <ToastMsg $show={!!toastMsg}>{toastMsg}</ToastMsg>
          {isEditMode && (
            <EditBar>
              <DockLabel style={{ margin: 0 }}>꾹 눌러 이동 · − 로 제거</DockLabel>
              <EditDoneBtn onClick={(e) => { e.stopPropagation(); exitEditMode(); }}>
                ✓ 완료
              </EditDoneBtn>
            </EditBar>
          )}
          <AppGrid>
            {mainApps.map((app, i) => (
              <AppItem
                layout
                key={app.id}
                data-app-id={app.id}
                $isDragged={draggedId === app.id}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handlePressStart(app.id, false);
                  handlePointerDown(e, app.id);
                }}
                onPointerMove={handlePointerMove}
                onPointerUp={(e) => { handlePointerUp(e); handlePressEnd(); }}
                onPointerCancel={(e) => { handlePointerCancel(e); handlePressEnd(); }}
                onClick={(e) => {
                  if (isEditMode) { e.stopPropagation(); return; }
                  if (app.id === 'decorateMailbox') {
                    setShowPreparing(true);
                  } else if (app.id === 'beta_history') {
                    localStorage.setItem(BETA_SEEN_KEY, LATEST_BETA_VERSION);
                    setBetaUnread(false);
                    navigateWithFade('/beta');
                  } else if (app.id === 'widget_settings') {
                    openWidgetMenu();
                  } else if (app.id === 'journal') {
                    openJournalApp();
                  } else if (app.action) {
                    app.action();
                  } else {
                    navigateWithFade(app.href || '#');
                  }
                }}
                style={{ touchAction: isEditMode ? 'none' : 'auto' }}
              >
                <AppItemContent $delay={i} $isEditMode={isEditMode}>
                  <AppIcon $color={app.color} $isEditMode={isEditMode}
                    style={holdingId === app.id ? { animation: 'none' } : {}}
                  >
                    {app.icon}
                    {app.id === 'beta_history' && betaUnread && <UnreadDot />}

                    {isEditMode && (
                      <BadgeBtn onClick={(e) => { e.stopPropagation(); handleMoveToFolder(app.id); }}>
                        -
                      </BadgeBtn>
                    )}
                  </AppIcon>
                  <AppName className="dock-app-name">{app.name}</AppName>
                </AppItemContent>
              </AppItem>
            ))}

          </AppGrid>
        </DockArea>

        <FolderOverlay $isOpen={isFolderOpen} onClick={() => setIsFolderOpen(false)}>
          <AnimatePresence>
            {isFolderOpen && (
              <FolderContainer
                onClick={e => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.88, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.88, y: 16 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              >
                <FolderHeader>
                  <FolderTitle>{t.home.apps.more}</FolderTitle>
                  <FolderSubtitle>꾹 눌러서 바탕화면으로 꺼내기</FolderSubtitle>
                </FolderHeader>
                <FolderGrid>
                  {folderApps.filter(a => !a.id.startsWith('__EMPTY__')).map((app, i) => (
                    <AppItem
                      key={app.id}
                      layoutId={`app-folder-${app.id}`}
                      onMouseDown={() => handlePressStart(app.id, true)}
                      onMouseUp={handlePressEnd}
                      onMouseLeave={handlePressEnd}
                      onTouchStart={() => handlePressStart(app.id, true)}
                      onTouchEnd={handlePressEnd}
                      onClick={(e) => {
                        if (app.id === 'decorateMailbox') {
                          setShowPreparing(true);
                        } else if (app.id === 'beta_history') {
                          localStorage.setItem(BETA_SEEN_KEY, LATEST_BETA_VERSION);
                          setBetaUnread(false);
                          navigateWithFade('/beta');
                        } else if (app.id === 'widget_settings') {
                          setIsFolderOpen(false);
                          openWidgetMenu();
                        } else if (app.id === 'journal') {
                          setIsFolderOpen(false);
                          openJournalApp();
                        } else if (app.action) {
                          app.action();
                        } else {
                          navigateWithFade(app.href || '#');
                        }
                      }}
                    >
                      <AppItemContent $delay={i}>
                        <AppIcon $color={app.color}>
                          {app.icon}
                          {app.id === 'beta_history' && betaUnread && <UnreadDot />}
                        </AppIcon>
                        <AppName>{app.name}</AppName>
                      </AppItemContent>
                    </AppItem>
                  ))}
                </FolderGrid>
              </FolderContainer>
            )}
          </AnimatePresence>
        </FolderOverlay>

        <AnimatePresence>
          {showJournalApp && (
            <JournalApp
              source={journalSource || 'desktop'}
              initialJournalId={selectedJournalId}
              onClose={() => {
                closeJournalApp();
              }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showBetaNotice && (
            <NoticeOverlay $isOpen={showBetaNotice}>
              <NoticeCard
                initial={{ opacity: 0, scale: 0.88, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.88, y: 20 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              >
                <NoticeDesc>
                  {t.home.betaNotice.desc.split('\n').map((line: string, i: number, arr: string[]) => (
                    <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
                  ))}
                </NoticeDesc>
                <DiscordBtn href="https://discord.gg/h3zZdMXAvD" target="_blank" rel="noopener noreferrer">
                  <svg width="24" height="24" viewBox="0 0 127.14 96.36" fill="#fff">
                    <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83A109.28 109.28 0 0 0 46 6.83 72.3 72.3 0 0 0 42.64 0a105.73 105.73 0 0 0-26.26 8.09C2.04 29.36-2.4 53.99.96 77.7a105.43 105.43 0 0 0 32.14 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a67.55 67.55 0 0 1-10.87 5.19 77.67 77.67 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c3.92-26.67-3.03-50.69-16.29-69.64zM42.45 65.69C36.18 65.69 31 60 31 53.01s5.02-12.68 11.45-12.68c6.48 0 11.58 5.76 11.45 12.68 0 6.99-5.02 12.68-11.45 12.68zm42.24 0c-6.27 0-11.45-5.68-11.45-12.68s5.02-12.68 11.45-12.68c6.48 0 11.58 5.76 11.45 12.68 0 6.99-5.02 12.68-11.45 12.68z" />
                  </svg>
                  {t.home.betaNotice.discordBtn}
                </DiscordBtn>
                <NoticeBtn onClick={handleCloseBetaNotice}>{t.home.betaNotice.confirmBtn}</NoticeBtn>
              </NoticeCard>
            </NoticeOverlay>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showWidgetMenu && (
            <NoticeOverlay $isOpen={showWidgetMenu} onClick={closeWidgetMenu}>
              <NoticeCard
                onClick={e => e.stopPropagation()}
                style={{ width: '90%', maxWidth: '400px', padding: '24px' }}
                initial={{ opacity: 0, scale: 0.88, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.88, y: 20 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              >
                <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 20 }}>위젯 추가</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', width: '100%', maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden', paddingBottom: '20px' }}>
                  {WIDGET_DEFS.map(w => (
                    <div
                      key={w.type}
                      onClick={() => handleAddWidget(w.type, w.defaultSize)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer',
                        background: '#EDE9D5', borderRadius: '16px', padding: '16px 8px', transition: 'transform 0.1s'
                      }}
                      onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.96)')}
                      onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <WidgetPreview type={w.type} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#5C5040' }}>{w.name}</span>
                    </div>
                  ))}
                </div>
              </NoticeCard>
            </NoticeOverlay>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showPreparing && (
            <NoticeOverlay $isOpen={showPreparing} onClick={() => setShowPreparing(false)}>
              <NoticeCard
                onClick={e => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.88, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.88, y: 20 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              >
                <NoticeDesc>아직 준비중인 기능입니다.</NoticeDesc>
                <NoticeBtn onClick={() => setShowPreparing(false)}>확인</NoticeBtn>
              </NoticeCard>
            </NoticeOverlay>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {confirmLogout && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(40,30,20,0.55)',
                backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px', boxSizing: 'border-box'
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.88, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.88, y: 20 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                style={{
                  background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
                  width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
                }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14, color: '#E07040'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </div>
                <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>로그아웃</p>
                <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22 }}>로그아웃 하시겠습니까?</p>
                <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                  <button
                    onClick={() => setConfirmLogout(false)}
                    style={{
                      flex: 1, padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: '#EDE9D5', color: '#6C5D47', fontSize: 15, fontWeight: 700
                    }}
                  >취소</button>
                  <button
                    onClick={() => {
                      localStorage.removeItem('isLoggedIn');
                      sessionStorage.removeItem('isLoggedIn');
                      localStorage.removeItem('access_token');
                      sessionStorage.removeItem('access_token');
                      localStorage.removeItem('has_seen_beta_notice');
                      window.location.href = '/login';
                    }}
                    style={{
                      flex: 1, padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                      fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                    }}
                  >로그아웃</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Full Screen Calendar ── */}
        {(showFullScreenCalendar || activeCalendarWidgetId) && (
          <FullScreenCalendar onClose={() => { setShowFullScreenCalendar(false); setActiveCalendarWidgetId(null); }} />
        )}

      </ResponsiveShell>

      {/* ── Page Fade Overlay ── */}
      <PageFadeOverlay $visible={pageFading} />
    </div>
  );
};

export default Home;
